/* eslint-disable react-hooks/exhaustive-deps */
// TOPO "Aurora" (modelo_topo = 10): barra de largura total, FIXA, do modelo "Aurora"
// (claro, arejado, cantos bem arredondados). Comportamento:
//  - na HOME, sobre um hero de mídia com véu escuro (13 "Aurora", 5, 8 e 11), a barra
//    nasce TRANSPARENTE (texto claro, logo como vier) e, ao rolar > 40px, vira barra
//    SÓLIDA na cor1 com sombra suave e altura menor (transição de 200ms);
//  - nas páginas internas (e na home com hero claro) já nasce sólida e reserva a própria
//    altura com um espaçador — o conteúdo nunca fica escondido atrás da barra fixa;
//  - desktop: logo à esquerda; menu ao centro em "pílulas" (fundo translúcido no hover e
//    na página atual; excedente num painel "Mais" arredondado com sombra); à direita o
//    atalho de favoritos com contador e um CTA em pílula (cor2) de WhatsApp/telefone;
//  - celular: logo + favoritos + hambúrguer, que reusa o DrawerComponent (setActiveDrawer).
// TUDO VARIÁVEL: menu = transações + menus do tenant + blog; telefone/WhatsApp e logo =
// dados do tenant (mesma origem do topo 7); cores = site.corN com contraste calculado
// (ensureReadableTextColor/isColorLight). Default export = ErrorBoundary com fallback pro
// Topo padrão (mesmo molde do TopoAutoral/TopoModerno).
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import React, { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { FaHeart, FaMapMarkerAlt, FaPhoneAlt, FaWhatsapp } from 'react-icons/fa';
import { ensureReadableTextColor, favoritosHref, isColorLight, MaskPhone } from '../../../utils/FunctionsGlobals';
import { trackMetaEvent } from '../../../utils/metaPixel';
import { resolveSiteWhatsAppUrl } from '../../../utils/site2524WhatsApp';
import { appendCodsiteQuery } from '../../../utils/url';
import { useAuth, useRegiao, useSiteRegiao } from '../../context/Info';
import { trackLead } from '../../whatsAppButton/WhatsAppButton';
import { temSeletorIdiomas } from '../../../utils/idiomaMoedaSite';
import { IdiomaNavItem } from '../idiomas/menu';
import Topo from '../topo/Topo';
import styles from './TopoAurora.module.css';

interface ITopoAurora {
    site: any;
    setActiveDrawer: any;
    setBuscaAvancada?: any;
    fallback?: React.ReactNode;
}

type NavItem = { label: string; href: string; external?: boolean };

// Heros em que a barra pode nascer transparente: todos têm mídia em tela cheia com véu
// escuro no alto, então o texto claro da barra lê sobre eles. Com qualquer outro hero (busca
// 1/2/3/9… têm fundo claro ou cartão colado no topo) a barra nasce sólida — texto branco
// sobre hero branco seria invisível.
const HEROS_COM_VEU_ESCURO = new Set<number>([5, 8, 11, 13]);

// Quantos itens do menu ficam visíveis na barra; o resto vai pro painel "Mais".
const ITENS_VISIVEIS = 5;
const ROLAGEM_PARA_SOLIDA = 40;

const buildWhatsHref = (site: any, phone?: string) => {
    const clean = (phone || '').replace(/\D/g, '');
    if (!clean) return '';
    const text =
        site?.texto_botao_whats ||
        `Olá, estou visitando o site da ${site?.Dados?.empresa || ''}, gostaria de um atendimento personalizado. Pode me ajudar?`;
    return `https://api.whatsapp.com/send?phone=55${clean}&text=${encodeURIComponent(text)}`;
};

// hex → rgba (pílula translúcida de hover na cor do texto calculado).
const hexToRgba = (hex: string | undefined | null, alpha: number) => {
    const fallback = `rgba(28, 34, 48, ${alpha})`;
    if (!hex) return fallback;
    let h = String(hex).trim().replace('#', '');
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (!/^[0-9a-fA-F]{6}$/.test(h)) return fallback;
    return `rgba(${parseInt(h.slice(0, 2), 16)}, ${parseInt(h.slice(2, 4), 16)}, ${parseInt(h.slice(4, 6), 16)}, ${alpha})`;
};

// Compara caminhos ignorando query/hash e barra final — decide qual pílula fica "atual".
const mesmaRota = (asPath: string, href: string): boolean => {
    const limpa = (v: string) => (v.split('?')[0].split('#')[0].replace(/\/+$/, '') || '/');
    return limpa(asPath) === limpa(href);
};

const TopoAuroraInner: React.FC<ITopoAurora> = ({ site: siteProp, setActiveDrawer }) => {
    // MULTI-REGIÃO: contatos (tel1/tel2) trocados pelos da região ativa quando houver.
    const site = useSiteRegiao(siteProp);
    const { allDataHome, favorito } = useAuth();
    const router = useRouter();
    const codsite = Array.isArray(router.query?.codsite) ? router.query?.codsite[0] : router.query?.codsite;
    // MULTI-FILIAIS (modo popup/tela): a filial ativa vira uma pílula na barra que abre o seletor
    // do site. Tenant sem regiões (ou no modo barra) ⇒ nomeFilial vazio ⇒ nada muda no markup.
    const { regiaoAtiva, regiaoMatriz, modoPopup, setFilialPopupAberto } = useRegiao();
    const nomeFilial = modoPopup ? regiaoAtiva?.nome || regiaoMatriz?.nome || '' : '';

    // codsite e favoritos só existem no cliente — anexa só após montar p/ o HTML do SSR
    // bater com a 1ª render do cliente (evita hydration mismatch).
    const [isMounted, setIsMounted] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [maisAberto, setMaisAberto] = useState(false);
    const maisRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        setIsMounted(true);
        const onScroll = () => setScrolled((window.scrollY || 0) > ROLAGEM_PARA_SOLIDA);
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        return () => window.removeEventListener('scroll', onScroll);
    }, []);
    // painel "Mais": fecha no clique fora e no Esc.
    useEffect(() => {
        if (!maisAberto) return;
        const fora = (e: MouseEvent) => {
            if (maisRef.current && !maisRef.current.contains(e.target as Node)) setMaisAberto(false);
        };
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setMaisAberto(false);
        };
        document.addEventListener('mousedown', fora);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', fora);
            document.removeEventListener('keydown', esc);
        };
    }, [maisAberto]);
    const withCodsite = (path: string) => (isMounted ? appendCodsiteQuery(path, codsite) : path);

    // Transparente só na home e só sobre hero de véu escuro; nas internas nasce sólida.
    const sobreHero = router.pathname === '/' && HEROS_COM_VEU_ESCURO.has(Number(site?.modelo_busca));
    const solida = !sobreHero || scrolled;

    // ---- cores: barra = cor1 com texto calculado; transparente = texto claro ----
    const corBarra = site?.cor1 || '#ffffff';
    const corBarraTexto = ensureReadableTextColor(isColorLight(corBarra) ? site?.cor11 : site?.cor13, corBarra) || '#1c2230';
    const corCta = site?.cor2 || site?.cor1 || '#2563eb';
    const corCtaTexto = ensureReadableTextColor(isColorLight(corCta) ? site?.cor11 : site?.cor13, corCta) || '#ffffff';
    const corTexto = solida ? corBarraTexto : '#ffffff';
    const corPilula = solida ? hexToRgba(corBarraTexto, 0.1) : 'rgba(255, 255, 255, 0.18)';
    // contador de favoritos: cor2 se contrastar com a barra; senão a cor do texto invertida.
    const corBadge = ensureReadableTextColor(corCta, solida ? corBarra : '#1c2230', 2.2, corTexto, corTexto) || corTexto;
    const corBadgeTexto = corBadge === corCta ? corCtaTexto : solida ? corBarra : '#1c2230';

    // ---- menu: transações + menus disponíveis do tenant + blog ----
    const navItems: NavItem[] = [];
    (site?.transacoes || []).forEach((t: string) => {
        if (!t) return;
        const low = String(t).toLowerCase();
        const label = low === 'venda' ? 'Comprar' : low === 'aluguel' ? 'Alugar' : low === 'temporada' ? 'Temporada' : t;
        navItems.push({ label, href: `/imoveis/${low}/-/-/-/-` });
    });
    (allDataHome?.menus || [])
        .filter((m: any) => m?.Disponivel && m?.titulo)
        .slice(0, 8)
        .forEach((m: any) => {
            navItems.push({ label: m.titulo, href: m?.link ? m.link : `/menu/${m.titulo}`, external: Boolean(m?.link && !String(m.link).startsWith('/')) });
        });
    if (allDataHome?.blogs?.[0]?.id) navItems.push({ label: 'Blog', href: '/blog' });
    const visiveis = navItems.length > ITENS_VISIVEIS + 1 ? navItems.slice(0, ITENS_VISIVEIS) : navItems;
    const excedentes = navItems.length > ITENS_VISIVEIS + 1 ? navItems.slice(ITENS_VISIVEIS) : [];
    const algumExcedenteAtivo = excedentes.some((i) => !i.external && mesmaRota(router.asPath || '/', i.href));

    // ---- CTA: 1º telefone com WhatsApp; sem WhatsApp, o 1º telefone vira ligação ----
    const phones = [
        { tel: site?.Dados?.tel1, nome: site?.Dados?.tel1_nome, whats: site?.Dados?.tel1_whats },
        { tel: site?.Dados?.tel2, nome: site?.Dados?.tel2_nome, whats: site?.Dados?.tel2_whats },
    ].filter((p) => p.tel);
    const whatsPhone = phones.find((p) => p.whats);
    const ctaPhone = whatsPhone || phones[0];
    const ctaIsWhats = Boolean(whatsPhone);
    // WhatsApp passa pelo redirecionador por site (cs2524), como no Topo clássico; demais sites: o mesmo link.
    const ctaHref = ctaPhone ? (ctaIsWhats ? resolveSiteWhatsAppUrl(site, buildWhatsHref(site, ctaPhone.tel)) : `tel:+55${(ctaPhone.tel || '').replace(/\D/g, '')}`) : '';
    const ctaLabel = MaskPhone(ctaPhone?.tel || '');

    const empresa = site?.Dados?.empresa || '';
    const logoSrc = site?.url_logo ? `${site?.url_logo}?v=${site?.contador_marca || 0}` : '';
    const totalFavoritos = isMounted && Array.isArray(favorito) ? favorito.length : 0;

    const renderItem = (item: NavItem, idx: number, classe: string) => {
        const ativo = !item.external && mesmaRota(router.asPath || '/', item.href);
        const cls = `${classe} ${ativo ? styles.navLinkAtivo : ''}`;
        return item.external ? (
            <a key={idx} href={item.href} className={cls} rel="noopener noreferrer" title={item.label} onClick={() => setMaisAberto(false)}>
                {item.label}
            </a>
        ) : (
            <Link
                prefetch={false}
                key={idx}
                href={withCodsite(item.href)}
                className={cls}
                title={item.label}
                aria-current={ativo ? 'page' : undefined}
                onClick={() => setMaisAberto(false)}
            >
                {item.label}
            </Link>
        );
    };

    const varsTema = {
        '--aurora-texto': corTexto,
        '--aurora-pilula': corPilula,
        '--aurora-barra': corBarra,
        '--aurora-barra-texto': corBarraTexto,
    } as CSSProperties;

    return (
        // BUILDER: âncora estável do bloco "topo" (cobre esta variante em qualquer página).
        <div className={styles.wrapper} data-builder-section="topo">
            {/* espaçador: só quando a barra nasce sólida (internas) — reserva a altura dela. */}
            {!sobreHero ? <div className={styles.spacer} aria-hidden="true" /> : null}

            <header
                className={`${styles.header} ${solida ? styles.solida : styles.transparente}`}
                style={{ ...varsTema, background: solida ? corBarra : 'transparent' }}
            >
                <div className={styles.inner}>
                    {/* logo à esquerda */}
                    <Link
                        prefetch={false}
                        href={withCodsite('/')}
                        onClick={() => trackMetaEvent(site, 'ViewContent', { content_name: 'Logo da imobiliária', content_category: 'header_navigation' })}
                        className={styles.logoLink}
                        title={`Imobiliária ${empresa}`}
                    >
                        {logoSrc ? (
                            <Image
                                src={logoSrc}
                                alt={`Imobiliária ${empresa}`}
                                title={`Imobiliária ${empresa}`}
                                className={styles.logo}
                                height={48}
                                width={200}
                                priority
                                fetchPriority="high"
                                unoptimized
                            />
                        ) : (
                            <span className={styles.logoText}>{empresa}</span>
                        )}
                    </Link>

                    {/* menu ao centro (desktop): pílulas + painel "Mais" */}
                    <nav className={styles.nav} aria-label="Menu principal">
                        {visiveis.map((item, idx) => renderItem(item, idx, styles.navLink))}
                        {excedentes.length > 0 ? (
                            <div
                                ref={maisRef}
                                className={styles.mais}
                                onMouseEnter={() => setMaisAberto(true)}
                                onMouseLeave={() => setMaisAberto(false)}
                            >
                                <button
                                    type="button"
                                    className={`${styles.navLink} ${styles.maisBotao} ${algumExcedenteAtivo ? styles.navLinkAtivo : ''}`}
                                    aria-haspopup="true"
                                    aria-expanded={maisAberto}
                                    onClick={() => setMaisAberto((v) => !v)}
                                >
                                    Mais
                                    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                        <polyline points="6 9 12 15 18 9" />
                                    </svg>
                                </button>
                                <div className={`${styles.painel} ${maisAberto ? styles.painelAberto : ''}`} role="menu">
                                    {excedentes.map((item, idx) => renderItem(item, idx, styles.painelLink))}
                                </div>
                            </div>
                        ) : null}
                    </nav>

                    {/* à direita: filial (multi-filiais) + favoritos + CTA */}
                    <div className={styles.acoes}>
                        {nomeFilial ? (
                            <button
                                type="button"
                                className={styles.filial}
                                aria-haspopup="dialog"
                                title="Trocar de filial"
                                aria-label={`Filial ${nomeFilial} — trocar de filial`}
                                onClick={() => setFilialPopupAberto(true)}
                            >
                                <FaMapMarkerAlt className={styles.filialPino} aria-hidden="true" />
                                <span className={styles.filialRotulo}>Filial</span>
                                <span className={styles.filialNome}>{nomeFilial}</span>
                                <svg className={styles.filialChevron} viewBox="0 0 12 12" width="10" height="10" aria-hidden="true" focusable="false">
                                    <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </button>
                        ) : null}
                        {/* 22/10: idioma no computador, na pílula do modelo com o suspenso do "Mais" (até 960px a
                            pílula some, como a da filial) */}
                        {temSeletorIdiomas(site) ? (
                            <IdiomaNavItem
                                ocultarAte={960}
                                classeWrapper={styles.mais}
                                classeBotao={styles.filial}
                                classePainel={`${styles.painel} ${styles.painelAberto}`}
                                classeOpcao={styles.painelLink}
                            />
                        ) : null}
                        {/* 22/10 (rodada 2): no celular o idioma fica NA BARRA, só a bandeira, ao lado dos favoritos e
                            do ☰ (a linha da gaveta saiu); suspenso padrão, sempre dentro da tela */}
                        {temSeletorIdiomas(site) ? <IdiomaNavItem ocultarAcima={960} compacto /> : null}
                        <Link
                            prefetch={false}
                            href={isMounted ? favoritosHref(favorito || [], codsite) : '/imoveis'}
                            className={styles.favoritos}
                            title="Clique aqui para ver seus imóveis favoritos"
                            aria-label={totalFavoritos > 0 ? `Imóveis favoritos (${totalFavoritos})` : 'Imóveis favoritos'}
                        >
                            <FaHeart aria-hidden="true" />
                            {totalFavoritos > 0 ? (
                                <span className={styles.badge} style={{ background: corBadge, color: corBadgeTexto }}>
                                    {totalFavoritos}
                                </span>
                            ) : null}
                        </Link>

                        {ctaPhone ? (
                            <a
                                className={styles.cta}
                                href={ctaHref}
                                style={{ background: corCta, color: corCtaTexto }}
                                rel="noopener noreferrer"
                                target={ctaIsWhats ? '_blank' : undefined}
                                title={ctaPhone.nome || (ctaIsWhats ? 'WhatsApp' : 'Telefone')}
                                aria-label={ctaIsWhats ? `Fale conosco pelo WhatsApp ${ctaLabel}` : `Ligar para ${ctaLabel}`}
                                onClick={() => {
                                    trackLead(site, null); // whatsapp_click no GTM/GA, como no Topo clássico (sem imóvel = sem Meta)
                                    trackMetaEvent(site, 'Contact', {
                                        content_name: 'Contato do cabeçalho',
                                        contact_method: ctaIsWhats ? 'whatsapp' : 'phone',
                                        content_category: 'header',
                                    });
                                }}
                            >
                                {ctaIsWhats ? <FaWhatsapp aria-hidden="true" /> : <FaPhoneAlt aria-hidden="true" />}
                                <span className={styles.ctaLabel}>{ctaLabel}</span>
                            </a>
                        ) : null}

                        {/* hambúrguer (celular) → drawer padrão do site */}
                        <button type="button" className={styles.hamburger} aria-label="Abrir menu" onClick={() => setActiveDrawer(true)}>
                            <span className={styles.hamburgerBar} />
                            <span className={styles.hamburgerBar} />
                            <span className={styles.hamburgerBar} />
                        </button>
                    </div>
                </div>
            </header>
        </div>
    );
};

// ErrorBoundary → fallback pro Topo padrão (mesmo padrão do TopoAutoral/TopoModerno).
class TopoAurora extends React.Component<ITopoAurora, { hasError: boolean }> {
    constructor(props: ITopoAurora) {
        super(props);
        this.state = { hasError: false };
    }
    static getDerivedStateFromError() {
        return { hasError: true };
    }
    componentDidCatch() {
        // silencioso: o topo degrada pro padrão sem quebrar a página.
    }
    render() {
        const { site, setActiveDrawer, fallback } = this.props;
        const defaultTopo = fallback ?? <Topo site={site} setActiveDrawer={setActiveDrawer} />;
        if (this.state.hasError || !site) return defaultTopo;
        return <TopoAuroraInner {...this.props} />;
    }
}

export default TopoAurora;
